/**
 * Transaction Status Enum
 * Tracks payment transaction progress through different stages
 * 
 * Status values:
 * 0: CREATED - Transaction record created
 * 1: PENDING - Waiting for user action
 * 2: IFRAME_SUBMITTED - User submitted payment form in iframe
 * 3: PROCESS_PAYMENT_V2_STARTED - ProcessPayment_V2 API call initiated
 * 4: PROCESS_PAYMENT_V2_FINISHED - ProcessPayment_V2 completed successfully
 * 5: CONVERT_RESERVATION_STARTED - ConvertReservationtoRentalAndSendLeaseAgreement API call initiated
 * 6: CONVERT_RESERVATION_FINISHED - ConvertReservationtoRentalAndSendLeaseAgreement completed successfully
 * 7: FAILED - Transaction failed at any stage
 * 8: COMPLETED - Entire payment flow completed successfully
 */

export const TRANSACTION_STATUS = {
  CREATED: 0,
  PENDING: 1,
  IFRAME_SUBMITTED: 2,
  PROCESS_PAYMENT_V2_STARTED: 3,
  PROCESS_PAYMENT_V2_FINISHED: 4,
  CONVERT_RESERVATION_STARTED: 5,
  CONVERT_RESERVATION_FINISHED: 6,
  FAILED: 7,
  COMPLETED: 8,
};

// Status labels for display/logging
export const TRANSACTION_STATUS_LABELS = {
  [TRANSACTION_STATUS.CREATED]: 'CREATED',
  [TRANSACTION_STATUS.PENDING]: 'PENDING',
  [TRANSACTION_STATUS.IFRAME_SUBMITTED]: 'IFRAME_SUBMITTED',
  [TRANSACTION_STATUS.PROCESS_PAYMENT_V2_STARTED]: 'PROCESS_PAYMENT_V2_STARTED',
  [TRANSACTION_STATUS.PROCESS_PAYMENT_V2_FINISHED]: 'PROCESS_PAYMENT_V2_FINISHED',
  [TRANSACTION_STATUS.CONVERT_RESERVATION_STARTED]: 'CONVERT_RESERVATION_STARTED',
  [TRANSACTION_STATUS.CONVERT_RESERVATION_FINISHED]: 'CONVERT_RESERVATION_FINISHED',
  [TRANSACTION_STATUS.FAILED]: 'FAILED',
  [TRANSACTION_STATUS.COMPLETED]: 'COMPLETED',
};

/**
 * Update transaction status in the database
 * @param {string} orderId - The order/transaction ID
 * @param {number} status - The status enum value
 * @param {object} metadata - Optional metadata to store with status update
 * @returns {Promise} - API response
 */
export const updateTransactionStatus = async (orderId, status, metadata = {}) => {
  if (!orderId) {
    console.warn('updateTransactionStatus: orderId is required');
    return Promise.resolve();
  }

  try {
    const response = await fetch('/api/transaction-history/update-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        status: status,
        status_label: TRANSACTION_STATUS_LABELS[status],
        metadata: metadata,
        timestamp: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Failed to update transaction status:', {
        orderId,
        status,
        error: errorData,
      });
      // Don't throw - we don't want to break payment flow if status update fails
      return Promise.resolve();
    }

    return response.json();
  } catch (error) {
    console.error('Error updating transaction status:', {
      orderId,
      status,
      error: error.message,
    });
    // Don't throw - we don't want to break payment flow if status update fails
    return Promise.resolve();
  }
};

/**
 * Create a new transaction history record
 * @param {object} transactionData - Transaction data including order_id, location_code, tenant_id, amount, etc.
 * @returns {Promise} - API response with transaction record
 */
export const createTransactionHistory = async (transactionData) => {
  try {
    const response = await fetch('/api/transaction-history/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...transactionData,
        status: TRANSACTION_STATUS.CREATED,
        status_label: TRANSACTION_STATUS_LABELS[TRANSACTION_STATUS.CREATED],
        created_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Failed to create transaction history:', errorData);
      return Promise.resolve(null);
    }

    return response.json();
  } catch (error) {
    console.error('Error creating transaction history:', error.message);
    return Promise.resolve(null);
  }
};

/**
 * Generate a unique order ID if one doesn't exist
 * @returns {string} - Order ID
 */
export const generateOrderId = () => {
  return Math.floor(Math.random() * 10000000000) + '';
};

/**
 * Get or generate order ID from session storage
 * @param {string} key - Session storage key (optional, defaults to 'current_order_id')
 * @returns {string} - Order ID
 */
export const getOrCreateOrderId = (key = 'current_order_id') => {
  let orderId = sessionStorage.getItem(key);
  if (!orderId) {
    orderId = generateOrderId();
    sessionStorage.setItem(key, orderId);
  }
  return orderId;
};
